/**
 * Age -> assessment band.
 *
 * The band is never typed by hand twice. A date of birth is the source of
 * truth; a plain age is accepted as a fallback for older profiles that only
 * ever captured a number.
 */

import type { Band } from "@/features/assessment-v2/band";
import { bandParam, BAND_LABEL } from "@/features/assessment-v2/band";

/** Whole years between a date of birth and today. Null when unparseable. */
export function ageFromDob(dob: string | null | undefined, now = new Date()): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  if (age < 0 || age > 120) return null;
  return age;
}

/** The band a given age belongs to. Under 12 is served by the Explorer band. */
export function bandFromAge(age: number | null | undefined): Band | null {
  if (age == null || !Number.isFinite(age)) return null;
  if (age >= 18) return "18plus";
  if (age >= 15) return "15to18";
  if (age >= 9) return "12to14";
  return null;
}

export type BandRecommendation = {
  age: number | null;
  band: Band | null;
  label: string | null;
  /** Route that starts the right assessment for this person. */
  href: string;
  /** True when the learner is a minor and guardian details should be asked for. */
  needsGuardian: boolean;
};

/**
 * Everything the profile screen needs to point someone at the right test.
 * `dob` wins; `age` is the legacy fallback.
 */
export function recommendBand(
  dob: string | null | undefined,
  age?: string | number | null,
): BandRecommendation {
  const fromDob = ageFromDob(dob);
  const fallback =
    fromDob == null && age != null && `${age}`.trim() !== ""
      ? Number.parseInt(`${age}`.replace(/\D+/g, ""), 10)
      : null;
  const resolved = fromDob ?? (Number.isFinite(fallback as number) ? (fallback as number) : null);
  const band = bandFromAge(resolved);
  return {
    age: resolved,
    band,
    label: band ? BAND_LABEL[band] : null,
    href: band ? (band === "18plus" ? "/assessment/v2" : `/assessment/v2?band=${bandParam(band)}`) : "/start",
    needsGuardian: resolved != null && resolved < 18,
  };
}
